import '../App.css';

import { useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';
import FoodItem from '../components/FoodItem';
import Header from '../components/Header';
import { useAuth } from '../hooks/useAuth';
import { useSeo } from '../hooks/useSeo';
import { buildSeoUrl, seoConfig } from '../utils/seoConfig';
import { captureProductEvent } from '../utils/productAnalytics';

const FALLBACK_RELATED_DISHES = [
  { slug: 'pasta-carbonara', name: 'Pasta Carbonara' },
  { slug: 'pizza-margherita', name: 'Pizza Margherita' },
  { slug: 'tonkotsu-ramen-black', name: 'Tonkotsu Ramen' },
  { slug: 'poke-bowl', name: 'Poke Bowl' },
];

const SCANNER_COPY = {
  es: { eyebrow: '¿Lo has visto en una carta?', title: 'Escanea la carta completa', button: 'Escanear mi carta' },
  ca: { eyebrow: "L'has vist en una carta?", title: 'Escaneja la carta completa', button: 'Escanejar la meva carta' },
  fr: { eyebrow: 'Vu sur un menu ?', title: 'Scannez le menu complet', button: 'Scanner mon menu' },
  de: { eyebrow: 'Auf einer Speisekarte entdeckt?', title: 'Scanne die ganze Speisekarte', button: 'Speisekarte scannen' },
  it: { eyebrow: 'Lo hai visto su un menu?', title: 'Scansiona il menu completo', button: 'Scansiona il menu' },
  en: { eyebrow: 'Found this dish on a menu?', title: 'Scan the full menu', button: 'Scan my menu' },
};

const WhatIs = () => {
  const { isLoading, isAuthenticated } = useAuth();
  const { dish } = useParams();
  const [foodData, setFoodData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const formatDishName = dishName => {
    if (!dishName) return '';
    return dishName
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formattedDish = formatDishName(dish);
  const languagePrefix = typeof navigator === 'undefined' ? 'en' : navigator.language?.split('-')[0];
  const scannerCopy = SCANNER_COPY[languagePrefix] || SCANNER_COPY.en;
  const scannerUrl = `/?entry=what_is&dish=${encodeURIComponent(dish || '')}`;
  const relatedDishes = FALLBACK_RELATED_DISHES.filter(item => item.slug !== dish).slice(0, 3);

  const generateMetaData = () => {
    const dishLabel = formattedDish || 'this dish';
    const baseTitle = formattedDish ? `What is ${formattedDish}?` : 'What is this dish?';
    const baseDescription = foodData
      ? `Learn about ${dishLabel}: ${
          foodData.description || `Discover nutritional information, ingredients, and more about ${dishLabel}.`
        }`
      : `Discover what ${dishLabel} is. Get nutrition, ingredients, allergens, and cooking tips.`;
    const canonicalPath = dish ? `/what-is/${dish}` : '/what-is';

    return {
      title: `${baseTitle} | ${seoConfig.siteName}`,
      description: `🍴 ${baseDescription}`.slice(0, 160),
      canonicalUrl: buildSeoUrl(canonicalPath),
      ogImage: foodData?.imageLink || seoConfig.defaultImage,
    };
  };

  const metaData = generateMetaData();
  const errorMetaData = {
    ...metaData,
    title: formattedDish || seoConfig.siteName,
  };
  const resolvedMeta = error ? errorMetaData : metaData;
  const ogType = error ? 'website' : 'article';
  const robots = error ? 'noindex, follow' : 'index, follow';
  const imageAlt = formattedDish ? `${formattedDish} nutrition details` : 'Dish nutrition details';
  const keywords = formattedDish
    ? `${formattedDish}, food, nutrition, ingredients, recipe, allergens`
    : 'food, nutrition, ingredients, recipe, allergens';

  useEffect(() => {
    const fetchFoodData = async () => {
      if (!dish) {
        setError('No dish specified');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`/get-food-information?dish=${encodeURIComponent(dish)}`);

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error(`Information for "${dish}" not found`);
          }
          throw new Error('Failed to fetch food information');
        }

        const data = await response.json();
        setFoodData(data);
      } catch (err) {
        console.error('Error fetching food data:', err);
        setError(err.message || 'Failed to load food information');
      } finally {
        setLoading(false);
      }
    };

    fetchFoodData();
  }, [dish]);

  const generateStructuredData = () => {
    if (!foodData) return null;

    const dishLabel = formattedDish || 'Dish';

    return {
      '@context': 'https://schema.org',
      '@type': 'Recipe',
      name: dishLabel,
      description: foodData.description || `Detailed information about ${dishLabel}`,
      image: foodData.imageLink || metaData.ogImage,
      author: {
        '@type': 'Organization',
        name: seoConfig.siteName,
      },
      nutrition: {
        '@type': 'NutritionInformation',
        calories: foodData.nutritional_info?.calories || null,
        proteinContent: foodData.nutritional_info?.protein || null,
        carbohydrateContent: foodData.nutritional_info?.carbohydrates || null,
        fatContent: foodData.nutritional_info?.fat || null,
      },
      recipeIngredient: foodData.ingredients || [],
      keywords: `${dishLabel}, food, nutrition, ingredients, recipe`,
    };
  };

  const structuredData = !error && foodData ? generateStructuredData() : null;
  const trackScannerCta = placement => {
    captureProductEvent('seo-scanner-cta-clicked', {
      placement,
      dish,
      dishNameLength: formattedDish.length,
      entryPoint: 'what_is',
      pageLanguage: languagePrefix,
    });
  };

  const trackRelatedDish = relatedDish => {
    captureProductEvent('seo-related-dish-clicked', {
      sourceDish: dish,
      relatedDish: relatedDish.slug,
      placement: 'what_is_related',
    });
  };

  useSeo({
    title: resolvedMeta.title,
    description: resolvedMeta.description,
    canonical: resolvedMeta.canonicalUrl,
    robots,
    keywords,
    og: {
      title: resolvedMeta.title,
      description: resolvedMeta.description,
      url: resolvedMeta.canonicalUrl,
      image: resolvedMeta.ogImage,
      imageAlt,
      type: ogType,
      siteName: seoConfig.siteName,
      locale: seoConfig.locale,
    },
    twitter: {
      card: 'summary_large_image',
      title: resolvedMeta.title,
      description: resolvedMeta.description,
      url: resolvedMeta.canonicalUrl,
      image: resolvedMeta.ogImage,
      imageAlt,
      site: seoConfig.twitterHandle,
      creator: seoConfig.twitterHandle,
      domain: seoConfig.twitterDomain,
    },
    structuredData,
  });

  if (loading) {
    return (
      <div className="App">
        <Header isLoading={isLoading} isAuthenticated={isAuthenticated} />
        <main className="main-content">
          <div className="container">
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <div style={{ textAlign: 'center' }}>
                <div className="loading-spinner" style={{ marginBottom: '20px' }}>
                  Loading...
                </div>
                <p>Loading information about {formatDishName(dish)}...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <Header isLoading={isLoading} isAuthenticated={isAuthenticated} />
        <main className="main-content">
          <div className="container">
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <div style={{ textAlign: 'center', maxWidth: '500px' }}>
                <div className="error-icon" style={{ fontSize: '48px', marginBottom: '20px' }}>
                  ❌
                </div>
                <h2>Food Information Not Found</h2>
                <p style={{ color: '#666', marginBottom: '20px' }}>{error}</p>
                <p style={{ color: '#888', fontSize: '14px' }}>Try visiting one of our available dishes:</p>
                <div style={{ marginTop: '20px' }}>
                  <a href="/what-is/pasta-carbonara" style={{ marginRight: '10px', color: '#007bff' }}>
                    Pasta Carbonara
                  </a>
                  <a href="/what-is/pizza-margherita" style={{ color: '#007bff' }}>
                    Pizza Margherita
                  </a>
                </div>
              </div>
            </div>
          </div>
        </main>
        <footer className="footer">
          <p>
            Made with ❤️ by{' '}
            <a href="https://x.com/jordibosch20" target="_blank" rel="noopener noreferrer">
              @jordibosch20
            </a>
          </p>
        </footer>
      </div>
    );
  }

  return (
    <div className="App">
      <Header isLoading={isLoading} isAuthenticated={isAuthenticated} />

      <main className="main-content">
        <div className="container">
          <section className="what-is-scanner-card what-is-scanner-card-top">
            <div className="what-is-scanner-copy">
              <span>{scannerCopy.eyebrow}</span>
              <h2>{scannerCopy.title}</h2>
              <p>Get photos, plain-language descriptions, nutrition and allergen hints for every dish.</p>
            </div>
            <a href={scannerUrl} onClick={() => trackScannerCta('what_is_top')} className="what-is-scanner-button">
              <span aria-hidden="true">📸</span> {scannerCopy.button}
            </a>
          </section>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
            <FoodItem foodData={foodData} />
          </div>
          <section className="what-is-example-card">
            <div className="what-is-example-image-wrap">
              <img
                src={foodData?.imageLink || foodData?.imageUrls?.[0] || metaData.ogImage}
                alt={`${formattedDish} example result`}
              />
            </div>
            <div>
              <span className="what-is-example-label">Example scan result</span>
              <h2>Imagine the whole menu explained like this</h2>
              <p>
                One photo creates a visual guide you can browse by dish, dietary preference and potential allergens.
              </p>
              <a href={scannerUrl} onClick={() => trackScannerCta('what_is_example')} className="what-is-scanner-button">
                {scannerCopy.button}
              </a>
            </div>
          </section>

          <section className="what-is-related-section">
            <div>
              <span className="what-is-example-label">Keep exploring</span>
              <h2>Related dishes</h2>
            </div>
            <div className="what-is-related-links">
              {relatedDishes.map(relatedDish => (
                <a
                  key={relatedDish.slug}
                  href={`/what-is/${relatedDish.slug}`}
                  onClick={() => trackRelatedDish(relatedDish)}
                >
                  {relatedDish.name}<span aria-hidden="true"> →</span>
                </a>
              ))}
            </div>
          </section>

          <section className="what-is-scanner-card what-is-scanner-card-bottom">
            <div className="what-is-scanner-copy">
              <span>{scannerCopy.eyebrow}</span>
              <h2>Turn your menu into dish photos</h2>
            </div>
            <a href={scannerUrl} onClick={() => trackScannerCta('what_is_bottom')} className="what-is-scanner-button">
              {scannerCopy.button}
            </a>
          </section>
        </div>
      </main>

      <footer className="footer">
        <p>
          Made with ❤️ by{' '}
          <a href="https://x.com/jordibosch20" target="_blank" rel="noopener noreferrer">
            @jordibosch20
          </a>
        </p>
      </footer>
    </div>
  );
};

export default WhatIs;
